import React, { useState, useMemo } from "react";
import { useUser, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../api";

// Helper function to check if user has admin privileges
function checkIsAdmin(user) {
  if (!user) return false;
  
  // Special email check for backwards compatibility
  if (user?.email === '74spartan@gmail.com') {
    return true;
  }
  
  // Check if user has roles
  if (!user?.roles || !Array.isArray(user.roles)) {
    return false;
  }
  
  // Define admin role names to check for
  const adminRoleNames = ['admin', 'sysadmin', 'system-admin'];
  
  // Check roles - handle both string and object formats
  return user.roles.some(role => {
    if (typeof role === 'string') {
      return adminRoleNames.includes(role);
    } else if (typeof role === 'object' && role !== null) {
      return adminRoleNames.includes(role.key) || adminRoleNames.includes(role.name);
    }
    return false;
  });
}

// Helper function to get feature badges
function getFeatureBadges(config) {
  const features = [];
  
  if (config.enableFisdapShiftProcessing) features.push('FISDAP Shift Processing');
  if (config.enableShiftScheduling) features.push('Shift Scheduling');
  if (config.enablePatientEncounterTracking) features.push('Patient Encounters');
  if (config.enablePreceptorEvaluations) features.push('Preceptor Evaluations');
  if (config.cohortManagementEnabled) features.push('Cohort Management');
  if (config.enableCalendarIntegration) features.push('Calendar Integration');
  
  return features;
}

export default function InstitutionManagement() {
  const user = useUser(api);
  const navigate = useNavigate();
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterByFeature, setFilterByFeature] = useState('');
  
  // Merge functionality state
  const [similarInstitutions, setSimilarInstitutions] = useState(null);
  const [showSimilarInstitutions, setShowSimilarInstitutions] = useState(false);
  const [detectingDuplicates, setDetectingDuplicates] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeSourceInstitution, setMergeSourceInstitution] = useState(null);
  const [mergeTargetInstitution, setMergeTargetInstitution] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeMessage, setMergeMessage] = useState({ type: '', text: '' });
  const [mergingInProgress, setMergingInProgress] = useState(false);
  
  // Modal and form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newInstitution, setNewInstitution] = useState({
    name: '',
    abbreviation: '',
    contactEmail: '',
    contactPhone: '',
    template: 'custom',
    features: {
      enableFisdapShiftProcessing: false,
      enableShiftScheduling: false,
      enablePatientEncounterTracking: false,
      enablePreceptorEvaluations: false,
      cohortManagementEnabled: false,
      enableCalendarIntegration: false,
      enableClinicalQuickNotes: false,
      enablePreceptorScoring: false,
      accessCodeSystemEnabled: false,
      requirePreceptorApproval: false
    }
  });
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  // Authentication check
  const isAdmin = checkIsAdmin(user);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (user && !isAdmin) {
      navigate('/sign-in');
    }
  }, [user, isAdmin, navigate]);

  // Data fetching - get all institution configurations
  const [{ data: institutionConfigs, error: configsError, fetching: configsFetching }, refetchConfigs] = useFindMany(api.institutionConfiguration, {
    select: {
      id: true,
      institution: true,
      enableFisdapShiftProcessing: true,
      enableShiftScheduling: true,
      enablePatientEncounterTracking: true,
      enablePreceptorEvaluations: true,
      cohortManagementEnabled: true,
      enableCalendarIntegration: true,
      enableClinicalQuickNotes: true,
      enablePreceptorScoring: true,
      updatedAt: true,
      createdAt: true,
      configuredBy: {
        email: true,
        firstName: true,
        lastName: true
      }
    }
  });
  
  // Global action for creating institution configuration
  const [{ data: createResult, error: createError, fetching: createFetching }, createInstitutionConfig] = useGlobalAction(api.createNewInstitution);
  
  // Global actions for merge functionality
  const [{ data: similarData, error: similarError, fetching: similarFetching }, detectSimilarInstitutions] = useGlobalAction(api.detectSimilarInstitutions);
  const [{ data: previewData, error: previewError, fetching: previewFetching }, getInstitutionMergePreview] = useGlobalAction(api.getInstitutionMergePreview);
  const [{ data: mergeData, error: mergeError, fetching: mergeFetching }, mergeInstitutions] = useGlobalAction(api.mergeInstitutions);

  // Get all users to extract institution names
  const [{ data: allUsers, error: usersError, fetching: usersFetching }] = useFindMany(api.user, {
    select: {
      id: true,
      institution: true,
      createdAt: true,
      approvalStatus: true,
      roles: { key: true, name: true }
    }
  });

  // STCC Template Configuration
  const stccTemplate = {
    enableFisdapShiftProcessing: true,
    enableShiftScheduling: true,
    enablePatientEncounterTracking: true,
    enablePreceptorEvaluations: true,
    cohortManagementEnabled: true,
    enableCalendarIntegration: false,
    enableClinicalQuickNotes: true,
    enablePreceptorScoring: true,
    accessCodeSystemEnabled: true,
    requirePreceptorApproval: true
  };

  // Handle template selection
  const handleTemplateChange = (template) => {
    setNewInstitution(prev => ({
      ...prev,
      template,
      features: template === 'stcc' ? { ...stccTemplate } : {
        enableFisdapShiftProcessing: false,
        enableShiftScheduling: false,
        enablePatientEncounterTracking: false,
        enablePreceptorEvaluations: false,
        cohortManagementEnabled: false,
        enableCalendarIntegration: false,
        enableClinicalQuickNotes: false,
        enablePreceptorScoring: false,
        accessCodeSystemEnabled: false,
        requirePreceptorApproval: false
      }
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newInstitution.name.trim()) {
      setSubmitMessage({ type: 'error', text: 'Institution name is required' });
      return;
    }

    try {
      const configData = {
        institution: newInstitution.name.trim(),
        contactInformation: {
          email: newInstitution.contactEmail || '',
          phone: newInstitution.contactPhone || '',
          abbreviation: newInstitution.abbreviation || ''
        },
        ...newInstitution.features,
        brandingSettings: {},
        customFieldDefinitions: {},
        defaultShiftTypes: ["field", "hospital"],
        evaluationFormTypes: {},
        workflowSettings: {},
        complianceSettings: {}
      };

      await createInstitutionConfig(configData);
      
      setSubmitMessage({ type: 'success', text: 'Institution created successfully!' });
      
      // Reset form
      setNewInstitution({
        name: '',
        abbreviation: '',
        contactEmail: '',
        contactPhone: '',
        template: 'custom',
        features: {
          enableDocumentProcessing: false,
          enableShiftScheduling: false,
          enablePatientEncounterTracking: false,
          enablePreceptorEvaluations: false,
          cohortManagementEnabled: false,
          enableCalendarIntegration: false,
          enableClinicalQuickNotes: false,
          enablePreceptorScoring: false,
          accessCodeSystemEnabled: false,
          requirePreceptorApproval: false
        }
      });
      
      // Refresh the institution list
      refetchConfigs();
      
      // Close modal after short delay
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitMessage({ type: '', text: '' });
      }, 2000);
      
    } catch (error) {
      setSubmitMessage({ type: 'error', text: 'Failed to create institution. Please try again.' });
    }
  };

  // Handle feature toggle
  const handleFeatureToggle = (featureName) => {
    setNewInstitution(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureName]: !prev.features[featureName]
      }
    }));
  };

  // Handle duplicate detection
  const handleDetectDuplicates = async () => {
    setDetectingDuplicates(true);
    try {
      const result = await detectSimilarInstitutions();
      setSimilarInstitutions(result);
      setShowSimilarInstitutions(true);
    } catch (error) {
      console.error('Failed to detect duplicates:', error);
    } finally {
      setDetectingDuplicates(false);
    }
  };

  // Handle merge initialization
  const handleInitiateMerge = (institution) => {
    setMergeSourceInstitution(institution);
    setMergeTargetInstitution('');
    setMergePreview(null);
    setMergeMessage({ type: '', text: '' });
    setIsMergeModalOpen(true);
  };

  // Handle merge target selection
  const handleMergeTargetChange = async (targetName) => {
    setMergeTargetInstitution(targetName);
    setMergePreview(null);
    
    if (targetName && mergeSourceInstitution) {
      try {
        const preview = await getInstitutionMergePreview({
          sourceInstitution: mergeSourceInstitution.institution,
          targetInstitution: targetName
        });
        setMergePreview(preview);
      } catch (error) {
        setMergeMessage({ type: 'error', text: 'Failed to load merge preview' });
      }
    }
  };

  // Handle merge execution
  const handleExecuteMerge = async () => {
    if (!mergeSourceInstitution || !mergeTargetInstitution) return;
    
    setMergingInProgress(true);
    setMergeMessage({ type: '', text: '' });
    
    try {
      await mergeInstitutions({
        sourceInstitution: mergeSourceInstitution.institution,
        targetInstitution: mergeTargetInstitution
      });
      
      setMergeMessage({ type: 'success', text: 'Institutions merged successfully!' });
      
      // Refresh data
      refetchConfigs();
      
      // Close modal after delay
      setTimeout(() => {
        setIsMergeModalOpen(false);
        setMergingInProgress(false);
        setMergeMessage({ type: '', text: '' });
        
        // Re-detect duplicates if they were shown
        if (showSimilarInstitutions) {
          handleDetectDuplicates();
        }
      }, 2000);
      
    } catch (error) {
      setMergeMessage({ type: 'error', text: 'Failed to merge institutions. Please try again.' });
      setMergingInProgress(false);
    }
  };



  // Helper function to check if current user is institutional admin (not system admin)
  const isInstitutionalAdmin = useMemo(() => {
    if (!user) return false;
    
    // If they're system admin, they can see all institutions
    if (checkIsAdmin(user)) return false;
    
    // Check if they have admin role but not system-admin
    if (!user.roles || !Array.isArray(user.roles)) return false;
    
    return user.roles.some(role => {
      const roleName = typeof role === 'string' ? role : (role.key || role.name);
      return roleName === 'admin';
    });
  }, [user]);

  // Get current user's institution for filtering
  const currentUserInstitution = useMemo(() => {
    return user?.institution;
  }, [user]);

  // Helper function to determine institution status
  const getInstitutionStatus = (isConfigured, hasUsers) => {
    if (isConfigured && hasUsers) {
      return {
        color: '#10b981',
        bgColor: '#065f46',
        borderColor: '#10b981',
        textColor: '#d1fae5',
        label: '✓ Configured + Active',
        description: 'Fully operational',
        priority: 1
      };
    } else if (isConfigured && !hasUsers) {
      return {
        color: '#f59e0b',
        bgColor: '#78350f',
        borderColor: '#f59e0b',
        textColor: '#fef3c7',
        label: '⚠ Configured + No Users',
        description: 'Awaiting enrollment',
        priority: 2
      };
    } else {
      return {
        color: '#dc2626',
        bgColor: '#7f1d1d',
        borderColor: '#dc2626',
        textColor: '#fecaca',
        label: '⚠ Needs Configuration',
        description: 'Action required',
        priority: 3
      };
    }
  };

  // Create comprehensive institution statistics from ALL sources
  const institutionStats = useMemo(() => {
    if (!allUsers && !institutionConfigs) return [];

    // Get all unique institution names from users (filter out null/empty)
    const institutionNamesFromUsers = allUsers 
      ? [...new Set(
          allUsers
            .map(user => user.institution)
            .filter(institution => institution && institution.trim())
        )]
      : [];

    // Get all institution names from configurations
    const institutionNamesFromConfigs = institutionConfigs
      ? institutionConfigs.map(config => config.institution)
      : [];

    // Merge and deduplicate all institution names
    const allInstitutionNames = [...new Set([
      ...institutionNamesFromUsers,
      ...institutionNamesFromConfigs
    ])];

    // Filter institutions based on user permissions
    const filteredInstitutionNames = isInstitutionalAdmin && currentUserInstitution
      ? allInstitutionNames.filter(name => name === currentUserInstitution)
      : allInstitutionNames;

    // Create stats for each institution
    const stats = filteredInstitutionNames.map(institutionName => {
      // Find corresponding configuration (if exists)
      const config = institutionConfigs?.find(c => c.institution === institutionName);
      
      // Get users for this institution
      const institutionUsers = allUsers?.filter(user => user.institution === institutionName) || [];
      const approvedUsers = institutionUsers.filter(user => user.approvalStatus === 'approved');
      const pendingUsers = institutionUsers.filter(user => user.approvalStatus === 'pending');
      
      const isConfigured = !!config;
      const hasUsers = institutionUsers.length > 0;
      const status = getInstitutionStatus(isConfigured, hasUsers);
      
      return {
        // Use config data if available, otherwise create default structure
        id: config?.id || `unconfigured-${institutionName}`,
        institution: institutionName,
        isConfigured,
        hasUsers,
        status,
        totalUsers: institutionUsers.length,
        approvedUsers: approvedUsers.length,
        pendingUsers: pendingUsers.length,
        features: config ? getFeatureBadges(config) : [],
        updatedAt: config?.updatedAt || null,
        createdAt: config?.createdAt || null,
        configuredBy: config?.configuredBy || null,
        // Include all config fields for configured institutions
        ...(config || {})
      };
    });

    return stats.sort((a, b) => {
      // Sort by status priority first, then by name
      if (a.status.priority !== b.status.priority) {
        return a.status.priority - b.status.priority;
      }
      return a.institution.toLowerCase().localeCompare(b.institution.toLowerCase());
    });
  }, [allUsers, institutionConfigs, isInstitutionalAdmin, currentUserInstitution]);

  // Filter and sort institutions
  const filteredInstitutions = useMemo(() => {
    let filtered = institutionStats;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(inst => 
        inst.institution.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply feature filter
    if (filterByFeature) {
      filtered = filtered.filter(inst => 
        inst.features.some(feature => feature.toLowerCase().includes(filterByFeature.toLowerCase()))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.institution.toLowerCase();
          bValue = b.institution.toLowerCase();
          break;
        case 'users':
          aValue = a.totalUsers;
          bValue = b.totalUsers;
          break;
        case 'updated':
          aValue = new Date(a.updatedAt);
          bValue = new Date(b.updatedAt);
          break;
        default:
          aValue = a.institution.toLowerCase();
          bValue = b.institution.toLowerCase();
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });

    return filtered;
  }, [institutionStats, searchTerm, filterByFeature, sortBy, sortOrder]);

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    if (!institutionStats.length) return { totalInstitutions: 0, totalUsers: 0, totalApproved: 0, totalPending: 0 };

    return {
      totalInstitutions: institutionStats.length,
      totalUsers: institutionStats.reduce((sum, inst) => sum + inst.totalUsers, 0),
      totalApproved: institutionStats.reduce((sum, inst) => sum + inst.approvedUsers, 0),
      totalPending: institutionStats.reduce((sum, inst) => sum + inst.pendingUsers, 0),
      mostActiveInstitution: institutionStats.reduce((max, inst) => 
        inst.totalUsers > (max?.totalUsers || 0) ? inst : max, null
      )
    };
  }, [institutionStats]);

  // Error handling for data fetching
  if (configsError || usersError) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h2 style={{ color: '#ff4757', marginBottom: '1rem' }}>Error Loading Data</h2>
          <p style={{ marginBottom: '1.5rem' }}>
            {configsError ? 'Failed to load institution configurations.' : 'Failed to load user data.'}
          </p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link 
              to="/admin" 
              style={{
                display: 'inline-block',
                background: '#3b82f6',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Back to Admin
            </Link>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#374151',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!user || configsFetching || usersFetching) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Loading institution data...</div>
      </div>
    );
  }

  // Check if user has any admin access (system admin or institutional admin)
  const hasAdminAccess = isAdmin || isInstitutionalAdmin;

  // Unauthorized access
  if (!hasAdminAccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h2 style={{ color: '#ff4757', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ marginBottom: '1.5rem' }}>
            You need administrator privileges to access this page.
          </p>
          <Link 
            to="/admin" 
            style={{
              display: 'inline-block',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#111111',
      color: '#ffffff',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header with Navigation */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <Link 
              to="/admin" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#3b82f6',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              <span></span>
              Admin Dashboard
            </Link>
            <span style={{ color: '#666' }}>/</span>
            <span style={{ color: '#d1d5db' }}>Institution Management</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '700',
                margin: '0 0 0.5rem 0',
                color: '#ffffff'
              }}>Institution Management</h1>
              <p style={{
                fontSize: '14px',
                color: '#9ca3af',
                margin: '0'
              }}>
                Manage all institutions - configured, unconfigured, with or without users
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              {isAdmin && (
                <>
                  <button
                    onClick={handleDetectDuplicates}
                    disabled={detectingDuplicates}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: detectingDuplicates ? '#6b7280' : '#f59e0b',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: detectingDuplicates ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => !detectingDuplicates && (e.target.style.background = '#d97706')}
                    onMouseOut={(e) => !detectingDuplicates && (e.target.style.background = '#f59e0b')}
                  >
                    <span style={{ fontSize: '18px' }}>🔍</span>
                    {detectingDuplicates ? 'Detecting...' : 'Detect Duplicates'}
                  </button>
                  
                  <button
                    onClick={() => setIsModalOpen(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: '#059669',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#047857'}
                    onMouseOut={(e) => e.target.style.background = '#059669'}
                  >
                    <span style={{ fontSize: '18px' }}>+</span>
                    Add Institution
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats Dashboard */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Total Institutions</h3>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#3b82f6'
            }}>{overallStats.totalInstitutions}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Total Users</h3>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#10b981'
            }}>{overallStats.totalUsers}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Approved Users</h3>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#059669'
            }}>{overallStats.totalApproved}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Pending Users</h3>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#ff4757'
            }}>{overallStats.totalPending}</div>
          </div>
        </div>

        {/* Similar Institutions Alert */}
        {showSimilarInstitutions && similarInstitutions && (
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #f59e0b',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                margin: '0',
                color: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>⚠️</span>
                Similar Institutions Detected
              </h3>
              <button
                onClick={() => setShowSimilarInstitutions(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '18px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            {similarInstitutions.length === 0 ? (
              <div style={{ color: '#10b981', fontSize: '14px' }}>
                ✅ No duplicate institutions found
              </div>
            ) : (
              <div>
                <p style={{ color: '#d1d5db', marginBottom: '1rem', fontSize: '14px' }}>
                  Found {similarInstitutions.length} groups of similar institutions that may be duplicates:
                </p>
                {similarInstitutions.map((group, idx) => (
                  <div key={idx} style={{
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#ffffff'
                      }}>
                        Similarity Score: {Math.round(group.similarity * 100)}%
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      alignItems: 'center'
                    }}>
                      {group.institutions.map((inst, instIdx) => (
                        <span key={instIdx} style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: '#065f46',
                          color: '#d1fae5',
                          border: '1px solid #10b981'
                        }}>
                          {inst}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search and Filter Controls */}
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db' }}>
                Search Institutions
              </label>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  background: '#2a2a2a',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db' }}>
                Filter by Feature
              </label>
              <select
                value={filterByFeature}
                onChange={(e) => setFilterByFeature(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  background: '#2a2a2a',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              >
                <option value="">All Features</option>
                <option value="FISDAP Shift Processing">FISDAP Shift Processing</option>
                <option value="Shift Scheduling">Shift Scheduling</option>
                <option value="Patient Encounters">Patient Encounters</option>
                <option value="Preceptor Evaluations">Preceptor Evaluations</option>
                <option value="Cohort Management">Cohort Management</option>
                <option value="Calendar Integration">Calendar Integration</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db' }}>
                Sort By
              </label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split('-');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  background: '#2a2a2a',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="users-desc">Most Users</option>
                <option value="users-asc">Least Users</option>
                <option value="updated-desc">Recently Updated</option>
                <option value="updated-asc">Oldest Updated</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterByFeature('');
                  setSortBy('name');
                  setSortOrder('asc');
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  background: '#374151',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Institution Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredInstitutions.map((institution) => (
            <div
              key={institution.id}
              style={{
                background: '#1e1e1e',
                border: `2px solid ${institution.status.borderColor}`,
                borderRadius: '12px',
                padding: '1.5rem',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${institution.status.color}40`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Status Badge - Large and Prominent */}
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                padding: '0.5rem 1rem',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '700',
                background: institution.status.bgColor,
                color: institution.status.textColor,
                border: `2px solid ${institution.status.borderColor}`,
                textAlign: 'center',
                lineHeight: '1.2'
              }}>
                <div>{institution.status.label}</div>
                <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '2px' }}>
                  {institution.status.description}
                </div>
              </div>

              <div style={{ marginBottom: '1rem', marginRight: '140px' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  margin: '0 0 0.5rem 0',
                  color: '#ffffff'
                }}>
                  {institution.institution}
                </h3>
                <div style={{
                  fontSize: '14px',
                  color: '#9ca3af',
                  marginBottom: '0.5rem'
                }}>
                  {institution.isConfigured && institution.updatedAt 
                    ? `Last updated: ${new Date(institution.updatedAt).toLocaleDateString()}`
                    : institution.isConfigured
                      ? `Created: ${new Date(institution.createdAt).toLocaleDateString()}`
                      : 'No configuration found'
                  }
                </div>
              </div>

              {/* Warning Message for Unconfigured Institutions with Users */}
              {!institution.isConfigured && institution.hasUsers && (
                <div style={{
                  background: '#7f1d1d',
                  border: '1px solid #dc2626',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#fecaca',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    <span>⚠️</span>
                    <span>Action Required: This institution has {institution.totalUsers} users but no configuration!</span>
                  </div>
                </div>
              )}

              {/* No Users Message for Configured Institutions */}
              {institution.isConfigured && !institution.hasUsers && (
                <div style={{
                  background: '#78350f',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#fef3c7',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    <span>ℹ️</span>
                    <span>No users enrolled yet - ready for registration</span>
                  </div>
                </div>
              )}

              {/* User Statistics */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#2a2a2a',
                borderRadius: '8px',
                border: institution.hasUsers ? '1px solid #404040' : '1px dashed #404040'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: institution.hasUsers ? '#3b82f6' : '#6b7280'
                  }}>
                    {institution.totalUsers}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Total Users</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: institution.approvedUsers > 0 ? '#10b981' : '#6b7280'
                  }}>
                    {institution.approvedUsers}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Approved</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: institution.pendingUsers > 0 ? '#ff4757' : '#6b7280'
                  }}>
                    {institution.pendingUsers}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Pending</div>
                </div>
              </div>

              {/* Feature Badges */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  fontSize: '14px',
                  color: '#d1d5db',
                  marginBottom: '0.5rem',
                  fontWeight: '500'
                }}>
                  Enabled Features
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {institution.isConfigured ? (
                    institution.features.length > 0 ? (
                      institution.features.map((feature, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: '#065f46',
                            color: '#d1fae5',
                            border: '1px solid #10b981'
                          }}
                        >
                          {feature}
                        </span>
                      ))
                    ) : (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: '#374151',
                        color: '#9ca3af',
                        border: '1px solid #4b5563'
                      }}>
                        No features enabled
                      </span>
                    )
                  ) : (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: '#7f1d1d',
                      color: '#fecaca',
                      border: '1px solid #dc2626'
                    }}>
                      Configuration required
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Link
                  to={`/admin/institution-config/${encodeURIComponent(institution.institution)}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '1',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    background: institution.isConfigured ? '#3b82f6' : '#dc2626',
                    color: 'white',
                    textDecoration: 'none',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    border: institution.isConfigured ? 'none' : '2px solid #ff4757'
                  }}
                  onMouseOver={(e) => e.target.style.background = institution.isConfigured ? '#2563eb' : '#b91c1c'}
                  onMouseOut={(e) => e.target.style.background = institution.isConfigured ? '#3b82f6' : '#dc2626'}
                >
                  {institution.isConfigured ? '⚙️ Configure' : '🚨 Setup Now'}
                </Link>
                
                {isAdmin && filteredInstitutions.length > 1 && institution.isConfigured && (
                  <button
                    onClick={() => handleInitiateMerge(institution)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '8px',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      fontWeight: '500',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      minWidth: '70px'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#4b5563'}
                    onMouseOut={(e) => e.target.style.background = '#6b7280'}
                  >
                    Merge
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredInstitutions.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#9ca3af'
          }}>
            <div style={{ fontSize: '18px', marginBottom: '0.5rem' }}>
              No institutions found
            </div>
            <div style={{ fontSize: '14px' }}>
              {searchTerm || filterByFeature ? 'Try adjusting your search or filter criteria' : 'No institutions have been configured yet'}
            </div>
          </div>
        )}

        {/* Add Institution Modal - Only for System Admins */}
        {isModalOpen && isAdmin && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#1e1e1e',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', margin: '0', color: '#ffffff' }}>
                  Add New Institution
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSubmitMessage({ type: '', text: '' });
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                   D7
                </button>
              </div>

              {/* Submit Message */}
              {submitMessage.text && (
                <div style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  background: submitMessage.type === 'success' ? '#065f46' : '#7f1d1d',
                  border: `1px solid ${submitMessage.type === 'success' ? '#10b981' : '#dc2626'}`,
                  color: submitMessage.type === 'success' ? '#d1fae5' : '#fecaca'
                }}>
                  {submitMessage.text}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Institution Name */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                    Institution Name *
                  </label>
                  <input
                    type="text"
                    value={newInstitution.name}
                    onChange={(e) => setNewInstitution(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter institution name"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                      background: '#2a2a2a',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Institution Abbreviation */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                    Abbreviation/Code
                  </label>
                  <input
                    type="text"
                    value={newInstitution.abbreviation}
                    onChange={(e) => setNewInstitution(prev => ({ ...prev, abbreviation: e.target.value }))}
                    placeholder="e.g., STCC, MIT"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                      background: '#2a2a2a',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Contact Information */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={newInstitution.contactEmail}
                      onChange={(e) => setNewInstitution(prev => ({ ...prev, contactEmail: e.target.value }))}
                      placeholder="admin@institution.edu"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #404040',
                        background: '#2a2a2a',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newInstitution.contactPhone}
                      onChange={(e) => setNewInstitution(prev => ({ ...prev, contactPhone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #404040',
                        background: '#2a2a2a',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Template Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                    Setup Template
                  </label>
                  <select
                    value={newInstitution.template}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                      background: '#2a2a2a',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="custom">Custom Setup</option>
                    <option value="stcc">STCC Template</option>
                  </select>
                </div>

                {/* Feature Toggles */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', marginBottom: '1rem', fontSize: '14px', color: '#d1d5db', fontWeight: '500' }}>
                    Features to Enable
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {[
                      { key: 'enableFisdapShiftProcessing', label: 'FISDAP Shift Processing' },
                      { key: 'enableShiftScheduling', label: 'Shift Scheduling' },
                      { key: 'enablePatientEncounterTracking', label: 'Patient Encounters' },
                      { key: 'enablePreceptorEvaluations', label: 'Preceptor Evaluations' },
                      { key: 'cohortManagementEnabled', label: 'Cohort Management' },
                      { key: 'enableCalendarIntegration', label: 'Calendar Integration' },
                      { key: 'enableClinicalQuickNotes', label: 'Clinical Quick Notes' },
                      { key: 'enablePreceptorScoring', label: 'Preceptor Scoring' },
                      { key: 'accessCodeSystemEnabled', label: 'Access Code System' },
                      { key: 'requirePreceptorApproval', label: 'Preceptor Approval Required' }
                    ].map((feature) => (
                      <label key={feature.key} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '14px',
                        color: '#d1d5db',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={newInstitution.features[feature.key]}
                          onChange={() => handleFeatureToggle(feature.key)}
                          style={{
                            accentColor: '#3b82f6'
                          }}
                        />
                        {feature.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setSubmitMessage({ type: '', text: '' });
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                      background: '#374151',
                      color: '#ffffff',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createFetching || !newInstitution.name.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: createFetching || !newInstitution.name.trim() ? '#4b5563' : '#059669',
                      color: '#ffffff',
                      fontSize: '14px',
                      cursor: createFetching || !newInstitution.name.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {createFetching ? 'Creating...' : 'Create Institution'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Merge Institution Modal - Only for System Admins */}
        {isMergeModalOpen && mergeSourceInstitution && isAdmin && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#1e1e1e',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  margin: '0',
                  color: '#ffffff'
                }}>
                  Merge Institution: {mergeSourceInstitution.institution}
                </h2>
                <button
                  onClick={() => {
                    setIsMergeModalOpen(false);
                    setMergeMessage({ type: '', text: '' });
                  }}
                  disabled={mergingInProgress}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    fontSize: '24px',
                    cursor: mergingInProgress ? 'not-allowed' : 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Merge Message */}
              {mergeMessage.text && (
                <div style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  background: mergeMessage.type === 'success' ? '#065f46' : '#7f1d1d',
                  border: `1px solid ${mergeMessage.type === 'success' ? '#10b981' : '#dc2626'}`,
                  color: mergeMessage.type === 'success' ? '#d1fae5' : '#fecaca'
                }}>
                  {mergeMessage.text}
                </div>
              )}

              {/* Progress Bar */}
              {mergingInProgress && (
                <div style={{
                  background: '#2a2a2a',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#3b82f6', marginBottom: '0.5rem' }}>
                    Merging institutions...
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: '#374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 50%, #3b82f6 100%)',
                      animation: 'shimmer 2s infinite linear'
                    }} />
                  </div>
                </div>
              )}

              {!mergingInProgress && (
                <>
                  {/* Target Institution Selection */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '14px',
                      color: '#d1d5db',
                      fontWeight: '500'
                    }}>
                      Merge into which institution? *
                    </label>
                    <select
                      value={mergeTargetInstitution}
                      onChange={(e) => handleMergeTargetChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #404040',
                        background: '#2a2a2a',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select target institution</option>
                      {institutionStats
                        .filter(inst => inst.institution !== mergeSourceInstitution.institution)
                        .map(inst => (
                          <option key={inst.id} value={inst.institution}>
                            {inst.institution} ({inst.totalUsers} users)
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Merge Warning */}
                  <div style={{
                    background: '#7f1d1d',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontSize: '18px' }}>⚠️</span>
                      <strong style={{ color: '#fecaca' }}>Warning: This action cannot be undone</strong>
                    </div>
                    <p style={{ color: '#fecaca', fontSize: '14px', margin: '0' }}>
                      All users, data, and configurations from "{mergeSourceInstitution.institution}" 
                      will be transferred to the target institution. The source institution will be deleted.
                    </p>
                  </div>

                  {/* Merge Preview */}
                  {previewFetching && (
                    <div style={{
                      background: '#2a2a2a',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      marginBottom: '1.5rem',
                      color: '#d1d5db'
                    }}>
                      Loading merge preview...
                    </div>
                  )}

                  {mergePreview && (
                    <div style={{
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '1rem',
                        color: '#ffffff'
                      }}>
                        Merge Preview
                      </h3>

                      {/* User Impact */}
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '0.5rem',
                          color: '#d1d5db'
                        }}>
                          User Impact:
                        </h4>
                        <ul style={{
                          color: '#d1d5db',
                          fontSize: '14px',
                          paddingLeft: '1.5rem',
                          margin: '0'
                        }}>
                          <li>{mergePreview.usersToMove || 0} users will be moved</li>
                          <li>{mergePreview.configsToUpdate || 0} configurations will be updated</li>
                          <li>{mergePreview.dataRecordsToMove || 0} data records will be transferred</li>
                        </ul>
                      </div>

                      {/* Configuration Conflicts */}
                      {mergePreview.configConflicts && mergePreview.configConflicts.length > 0 && (
                        <div>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            marginBottom: '0.5rem',
                            color: '#f59e0b'
                          }}>
                            Configuration Conflicts:
                          </h4>
                          {mergePreview.configConflicts.map((conflict, idx) => (
                            <div key={idx} style={{
                              background: '#374151',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              marginBottom: '0.5rem',
                              fontSize: '14px'
                            }}>
                              <div style={{ color: '#ffffff', marginBottom: '0.25rem' }}>
                                {conflict.field}
                              </div>
                              <div style={{ color: '#9ca3af' }}>
                                Source: {String(conflict.sourceValue)} → Target: {String(conflict.targetValue)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Form Actions */}
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsMergeModalOpen(false);
                        setMergeMessage({ type: '', text: '' });
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        border: '1px solid #404040',
                        background: '#374151',
                        color: '#ffffff',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleExecuteMerge}
                      disabled={!mergeTargetInstitution || !mergePreview}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        border: 'none',
                        background: (!mergeTargetInstitution || !mergePreview) ? '#4b5563' : '#dc2626',
                        color: '#ffffff',
                        fontSize: '14px',
                        cursor: (!mergeTargetInstitution || !mergePreview) ? 'not-allowed' : 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Confirm Merge
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <style>
          {`
            @keyframes shimmer {
              0% {
                background-position: -200% 0;
              }
              100% {
                background-position: 200% 0;
              }
            }
          `}
        </style>
      </div>
    </div>
  );
}
